import { useState, useEffect } from 'react';
import {
  Col,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Modal,
  ModalBody,
  Spinner,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { X, FilePlus } from 'react-feather';
import facturasApiService from '../../services/http/facturasApiService';
import logger from '../../services/logger';
import colors from '../../theme/colors';

const FormularioNuevaFactura = () => {
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState('CEDESCA');
  const [tipoCliente, setTipoCliente] = useState('Alumno');
  const [ccodcli, setCcodcli] = useState('');
  const [clienteEmpresa, setClienteEmpresa] = useState('');
  const [clientesEmpresa, setClientesEmpresa] = useState([]);
  const [importe, setImporte] = useState('');
  const [concepto, setConcepto] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [unidades, setUnidades] = useState('1');
  const [impuesto, setImpuesto] = useState('');
  const [irpf, setIrpf] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Formas de pago con código y descripción
  const formasPagoDummy = [
    { codigo: 0, descripcion: 'Contado efectivo' },
    { codigo: 1, descripcion: 'Tarjeta crédito' },
    { codigo: 2, descripcion: 'Recibo domiciliado' },
    { codigo: 3, descripcion: 'Transferencia' },
    { codigo: 4, descripcion: 'Talón o cheque' },
    { codigo: 5, descripcion: 'Financiado' },
    { codigo: 6, descripcion: 'Stripe' },
    { codigo: 7, descripcion: 'Pago débito' },
  ];

  // Cargar clientes cuando cambia la empresa o el tipo de cliente
  useEffect(() => {
    const fetchClientes = async () => {
      if (tipoCliente === 'Empresa' && empresa) {
        try {
          const clientes = await facturasApiService.cargarClientes({ headquarter: empresa });
          setClientesEmpresa(clientes);
        } catch (error) {
          logger.error('Error cargando clientes:', error);
          setClientesEmpresa([]);
        }
      } else {
        setClientesEmpresa([]);
      }
    };

    fetchClientes();
  }, [empresa, tipoCliente]);

  const validateForm = () => {
    const newErrors = {};

    if (!empresa) newErrors.empresa = 'La empresa es obligatoria';
    if (!tipoCliente) newErrors.tipoCliente = 'El tipo de cliente es obligatorio';

    if (tipoCliente === 'Alumno') {
      if (!ccodcli) {
        newErrors.ccodcli = 'El código de cliente es obligatorio';
      } else if (!/^\d+$/.test(ccodcli)) {
        newErrors.ccodcli = 'Solo se permiten números';
      }
    } else if (tipoCliente === 'Empresa') {
      if (!clienteEmpresa) newErrors.clienteEmpresa = 'Debe seleccionar un cliente';
    }

    if (!importe) {
      newErrors.importe = 'El importe es obligatorio';
    } else if (parseFloat(importe) <= 0) {
      newErrors.importe = 'El importe debe ser mayor que 0';
    }

    if (!unidades) {
      newErrors.unidades = 'Las unidades son obligatorias';
    } else if (parseFloat(unidades) < 1) {
      newErrors.unidades = 'Las unidades deben ser al menos 1';
    }

    if (!impuesto) {
      newErrors.impuesto = 'El impuesto es obligatorio';
    } else if (parseFloat(impuesto) < 0) {
      newErrors.impuesto = 'El impuesto no puede ser negativo';
    }

    if (!irpf) {
      newErrors.irpf = 'El IRPF es obligatorio';
    } else if (parseFloat(irpf) < 0) {
      newErrors.irpf = 'El IRPF no puede ser negativo';
    }

    if (!concepto) newErrors.concepto = 'El concepto es obligatorio';
    if (!formaPago) newErrors.formaPago = 'La forma de pago es obligatoria';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      setShowModal(true);

      try {
        const payload = {
          headquarter: empresa,
          tipoCliente,
          ccodcli: tipoCliente === 'Alumno' ? ccodcli : null,
          clienteEmpresa: tipoCliente === 'Empresa' ? clienteEmpresa : null,
          importe: parseFloat(importe),
          unidades: parseFloat(unidades),
          impuesto: parseFloat(impuesto),
          irpf: parseFloat(irpf),
          concepto,
          formaPago: parseInt(formaPago, 10),
        };

        const response = await facturasApiService.crearFacturaAbierta(payload);

        setShowModal(false);
        toast.success(response.message || 'Factura creada correctamente');

        // Redirigir después de crear la factura
        setTimeout(() => {
          navigate('/facturas');
        }, 2000);
      } catch (error) {
        setShowModal(false);
        logger.error('Error creando factura:', error);
        toast.error('Error al crear la factura');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Modal isOpen={showModal} centered>
        <ModalBody className="text-center p-4">
          <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3">Creando factura...</p>
        </ModalBody>
      </Modal>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="empresa">
                Empresa <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="empresa"
                value={empresa}
                onChange={(e) => {
                  setEmpresa(e.target.value);
                  setClienteEmpresa(''); // Resetear cliente seleccionado
                  setErrors({});
                }}
                invalid={!!errors.empresa}
              >
                <option value="">Seleccionar...</option>
                <option value="CEDESCA">CEDESCA</option>
                <option value="CNTEC">CNTEC</option>
              </Input>
              {errors.empresa && <FormFeedback>{errors.empresa}</FormFeedback>}
            </FormGroup>
          </Col>

          <Col md="6">
            <FormGroup>
              <Label for="tipoCliente">
                Tipo de Cliente <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="tipoCliente"
                value={tipoCliente}
                onChange={(e) => {
                  setTipoCliente(e.target.value);
                  setCcodcli('');
                  setClienteEmpresa('');
                  setErrors({});
                }}
                invalid={!!errors.tipoCliente}
              >
                <option value="">Seleccionar...</option>
                <option value="Alumno">Alumno</option>
                <option value="Empresa">Empresa</option>
              </Input>
              {errors.tipoCliente && <FormFeedback>{errors.tipoCliente}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          {tipoCliente === 'Alumno' ? (
            <Col md="6">
              <FormGroup>
                <Label for="ccodcli">
                  Código de Cliente <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="ccodcli"
                  value={ccodcli}
                  onChange={(e) => setCcodcli(e.target.value)}
                  invalid={!!errors.ccodcli}
                  placeholder="Ingrese solo números"
                />
                {errors.ccodcli && <FormFeedback>{errors.ccodcli}</FormFeedback>}
              </FormGroup>
            </Col>
          ) : tipoCliente === 'Empresa' ? (
            <Col md="6">
              <FormGroup>
                <Label for="clienteEmpresa">
                  Cliente <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="clienteEmpresa"
                  value={clienteEmpresa}
                  onChange={(e) => setClienteEmpresa(e.target.value)}
                  invalid={!!errors.clienteEmpresa}
                >
                  <option value="">Seleccionar cliente...</option>
                  {clientesEmpresa.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.value}
                    </option>
                  ))}
                </Input>
                {errors.clienteEmpresa && <FormFeedback>{errors.clienteEmpresa}</FormFeedback>}
              </FormGroup>
            </Col>
          ) : null}

          <Col md="6">
            <FormGroup>
              <Label for="importe">
                Precio <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                id="importe"
                value={importe}
                onChange={(e) => setImporte(e.target.value)}
                invalid={!!errors.importe}
                placeholder="0.00"
              />
              {errors.importe && <FormFeedback>{errors.importe}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="4">
            <FormGroup>
              <Label for="unidades">
                Unidades <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                step="1"
                min="1"
                id="unidades"
                value={unidades}
                onChange={(e) => setUnidades(e.target.value)}
                invalid={!!errors.unidades}
                placeholder="1"
              />
              {errors.unidades && <FormFeedback>{errors.unidades}</FormFeedback>}
            </FormGroup>
          </Col>

          <Col md="4">
            <FormGroup>
              <Label for="impuesto">
                Impuesto (%) <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                id="impuesto"
                value={impuesto}
                onChange={(e) => setImpuesto(e.target.value)}
                invalid={!!errors.impuesto}
                placeholder="0.00"
              />
              {errors.impuesto && <FormFeedback>{errors.impuesto}</FormFeedback>}
            </FormGroup>
          </Col>

          <Col md="4">
            <FormGroup>
              <Label for="irpf">
                IRPF (%) <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                id="irpf"
                value={irpf}
                onChange={(e) => setIrpf(e.target.value)}
                invalid={!!errors.irpf}
                placeholder="0.00"
              />
              {errors.irpf && <FormFeedback>{errors.irpf}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="12">
            <FormGroup>
              <Label for="concepto">
                Concepto <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                id="concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                invalid={!!errors.concepto}
                rows="3"
              />
              {errors.concepto && <FormFeedback>{errors.concepto}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="formaPago">
                Forma de Pago <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="formaPago"
                value={formaPago}
                onChange={(e) => setFormaPago(e.target.value)}
                invalid={!!errors.formaPago}
              >
                <option value="">Seleccionar...</option>
                {formasPagoDummy.map((forma) => (
                  <option key={forma.codigo} value={forma.codigo}>
                    {forma.descripcion}
                  </option>
                ))}
              </Input>
              {errors.formaPago && <FormFeedback>{errors.formaPago}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>

        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button
            type="button"
            onClick={() => navigate('/facturas')}
            disabled={isLoading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
              fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
              border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1, transition: 'all 0.15s',
            }}
          >
            <X size={13} />
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
              fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
              backgroundColor: colors.primary, color: '#fff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1, transition: 'all 0.15s',
            }}
          >
            {isLoading ? <Spinner style={{ height: '13px', width: '13px' }} /> : <FilePlus size={13} />}
            {isLoading ? 'Creando...' : 'Crear Factura'}
          </button>
        </div>
      </Form>
    </>
  );
};

export default FormularioNuevaFactura;
